type Color = string;

const DEFAULT_COLOR = "#000000";

export enum LessVarKeys {
  ACCENT_COLOUR = "skin-accent-background-color",
  HEADER_BUTTON_HOVER = "skin-site-header-hover-color",
  HEADER_BUTTON_NORMAL = "skin-site-header-color",
  HEADER_COLOUR = "skin-site-header-background-color",
  NAVIGATION_BUTTON = "skin-color-primary-action",
  PROGRESS_BAR = "skin-color-completed",
}

export interface IColor {
  title: string;
  description: string;
  color: string;
  ref: React.RefObject<HTMLInputElement>;
}

export interface ILessVariable {
  id: string;
  value: Color;
}

export type SelectableColors<T> = {
  [key in LessVarKeys]: T;
};

export const buildObjFrom = <T extends {}>(func: (key: LessVarKeys) => T): SelectableColors<T> => ({
  [LessVarKeys.HEADER_COLOUR]: func(LessVarKeys.HEADER_COLOUR),
  [LessVarKeys.ACCENT_COLOUR]: func(LessVarKeys.ACCENT_COLOUR),
  [LessVarKeys.HEADER_BUTTON_NORMAL]: func(LessVarKeys.HEADER_BUTTON_NORMAL),
  [LessVarKeys.HEADER_BUTTON_HOVER]: func(LessVarKeys.HEADER_BUTTON_HOVER),
  [LessVarKeys.PROGRESS_BAR]: func(LessVarKeys.PROGRESS_BAR),
  [LessVarKeys.NAVIGATION_BUTTON]: func(LessVarKeys.NAVIGATION_BUTTON),
});

export const defaultColors: SelectableColors<string> = {
  [LessVarKeys.ACCENT_COLOUR]: "#743276",
  [LessVarKeys.HEADER_BUTTON_HOVER]: "#5A92CB",
  [LessVarKeys.HEADER_BUTTON_NORMAL]: "#FFFFFF",
  [LessVarKeys.HEADER_COLOUR]: "#333333",
  [LessVarKeys.NAVIGATION_BUTTON]: "#6DC2FC",
  [LessVarKeys.PROGRESS_BAR]: "#44E689",
};

export const mapColorToLessVariable = (key: string, color: IColor, defaultColor = DEFAULT_COLOR): ILessVariable => ({
  id: key,
  value: color.ref.current?.value || defaultColor,
});

export const parseLessVariable = (key: string, defaultColor = DEFAULT_COLOR) => (text: string): ILessVariable => {
  const rgx = new RegExp(`@${key}\w*:\w*(.*);`);
  const result = text.match(rgx);
  return {
    id: key,
    value: result !== null && result.length > 0 ? result[1].trim() : defaultColor,
  };
};

export const mkLessVariable = (name: string, value: Color): ILessVariable => ({
  id: name,
  value,
});

export const parseSelectableColors = (text: string): SelectableColors<ILessVariable> =>
  buildObjFrom(key => parseLessVariable(key, defaultColors[key])(text));

export const interpolateVariable = (lessCode: string, lessVariable: ILessVariable): string => {
  const key = lessVariable.id;
  const rgx = new RegExp(`@${key}\w*:\w*(.*);`);
  const result = lessCode.match(rgx);
  if (!result) {
    return lessCode.concat(`\n@${key}: ${lessVariable.value};`);
  }
  return lessCode.replace(rgx, `@${key}: ${lessVariable.value};`);
};

export const interpolateSelectableColors = (
  lessCode: string,
  selectableColors: SelectableColors<ILessVariable>
): string => {
  return Object.values<ILessVariable>(selectableColors).reduce(interpolateVariable, lessCode);
};
